import { useEffect, useRef, useState } from 'react';

/**
 * True when a drag carries files from outside the browser. In-app drags (a
 * story moving between folders, a timeline element) carry their own MIME
 * types and never `Files`, so they are never mistaken for an upload.
 */
export function dragHasFiles(dataTransfer: DataTransfer | null): boolean {
  return !!dataTransfer && Array.from(dataTransfer.types).includes('Files');
}

/**
 * Lets a whole page accept dropped files (docs/design-system.md → Drop Zones).
 *
 * Listens on `window`, so the drop lands wherever the person lets go. `enabled`
 * says whether the page takes a drop right now; while it is false the hook
 * still swallows a file dropped on the page (showing a "no drop" cursor), so a
 * stray drop never makes the browser navigate away to the file — which would
 * also kill an upload in progress.
 *
 * `dragenter`/`dragleave` fire for every child element crossed, so a counter
 * rather than a boolean decides when the drag has really left the window.
 */
export function usePageFileDrop({
  enabled,
  onDrop,
}: {
  enabled: boolean;
  onDrop: (files: File[]) => void;
}) {
  const [isDragging, setIsDragging] = useState(false);
  const depthRef = useRef(0);
  const enabledRef = useRef(enabled);
  const onDropRef = useRef(onDrop);
  enabledRef.current = enabled;
  onDropRef.current = onDrop;

  useEffect(() => {
    if (!enabled) {
      depthRef.current = 0;
      setIsDragging(false);
    }
  }, [enabled]);

  useEffect(() => {
    const reset = () => {
      depthRef.current = 0;
      setIsDragging(false);
    };
    const handleEnter = (e: DragEvent) => {
      if (!dragHasFiles(e.dataTransfer) || !enabledRef.current) return;
      depthRef.current += 1;
      setIsDragging(true);
    };
    const handleOver = (e: DragEvent) => {
      if (!dragHasFiles(e.dataTransfer)) return;
      // An element that takes files itself (a dialog's drop box) already
      // handled it.
      if (e.defaultPrevented) return;
      e.preventDefault();
      if (e.dataTransfer) {
        e.dataTransfer.dropEffect = enabledRef.current ? 'copy' : 'none';
      }
    };
    const handleLeave = (e: DragEvent) => {
      if (!dragHasFiles(e.dataTransfer) || !enabledRef.current) return;
      depthRef.current = Math.max(0, depthRef.current - 1);
      if (depthRef.current === 0) setIsDragging(false);
    };
    const handleDrop = (e: DragEvent) => {
      if (!dragHasFiles(e.dataTransfer)) return;
      const handledByElement = e.defaultPrevented;
      e.preventDefault();
      reset();
      if (handledByElement || !enabledRef.current) return;
      const files = Array.from(e.dataTransfer?.files ?? []);
      if (files.length) onDropRef.current(files);
    };

    window.addEventListener('dragenter', handleEnter);
    window.addEventListener('dragover', handleOver);
    window.addEventListener('dragleave', handleLeave);
    window.addEventListener('drop', handleDrop);
    window.addEventListener('dragend', reset);
    window.addEventListener('blur', reset);
    return () => {
      window.removeEventListener('dragenter', handleEnter);
      window.removeEventListener('dragover', handleOver);
      window.removeEventListener('dragleave', handleLeave);
      window.removeEventListener('drop', handleDrop);
      window.removeEventListener('dragend', reset);
      window.removeEventListener('blur', reset);
    };
  }, []);

  return { isDragging };
}
